import axios from 'axios';
import { notifyConversationAccessDenied } from './conversationAccessDenied';
import { buildCanonicalCustomerConversationPath } from './customerConversationRoutes';
import getWebchatApiUrl from './getWebchatUrl';

interface GoToConversationOptions {
	customerId: string;
	navigate: (path: string) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onError?: (error: any) => void;
}

/**
 * Navigate to a customer's conversation in the inbox
 * @param customerId - The customer's ID
 * @param navigate - React Router's navigate function
 * @param onError - Optional error handler
 * @returns Promise that resolves when navigation is complete
 */
export const goToCustomerConversation = async ({
	customerId,
	navigate,
	onError,
}: GoToConversationOptions): Promise<void> => {
	if (!customerId) {
		console.warn('No customer ID provided');
		return;
	}

	try {
		const response = await axios({
			url: `${getWebchatApiUrl(
				window.location.href
			)}/api/webchat/get-customer-conversation-id`,
			method: 'post',
			data: { _customer: customerId },
			withCredentials: true,
		});

		if (response.data.accessDenied) {
			notifyConversationAccessDenied();
			return;
		}

		if (response.data.error) {
			throw new Error('Error getting customer messages');
		}

		const { _customerConversation, _business } = response.data;

		navigate(
			buildCanonicalCustomerConversationPath({
				_customerConversation,
				_business,
			})
		);
	} catch (err) {
		console.error('Error navigating to conversation:', err);
		if (onError) {
			onError(err);
		} else {
			alert('Could not load conversation. Please try again.');
		}
		throw err; // Re-throw to allow component to handle loading state
	}
};

/**
 * Extract customer ID from a customer object or ID string
 * @param customer - Customer object or ID string
 * @returns The customer ID string or null
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractCustomerId = (customer: any): string | null => {
	if (!customer) return null;

	if (typeof customer === 'string') {
		return customer;
	}

	if (typeof customer === 'object' && customer !== null && customer._id) {
		return customer._id;
	}

	return null;
};
